"use client";

import { useContext, useEffect } from "react";
import { AppContext } from "@/context/AppContext";
import { WorkCase } from "@/data/types";
import Link from "next/link";
import { useData, useLocalePath } from "@/i18n/LangContext";
import FloatingBackLink from "@/components/FloatingBackLink";
import Hero from "./workCaseDetail/Hero";
import MetaInfo from "./workCaseDetail/MetaInfo";
import ContentSection from "./workCaseDetail/ContentSection";
import CodeExampleSection from "./workCaseDetail/CodeExampleSection";
import ImageGallery from "./workCaseDetail/ImageGallery";
import DemoVideo from "./workCaseDetail/DemoVideo";
import NavigationFooter from "./workCaseDetail/NavigationFooter";
import TechnologiesSection from "./workCaseDetail/TechnologiesSection";

interface WorkCaseDetailClientProps {
  workCase: WorkCase;
}

export function WorkCaseDetailClient({ workCase }: WorkCaseDetailClientProps) {
  const { setActivePage } = useContext(AppContext);
  const { ui } = useData();
  const lp = useLocalePath();
  const content = workCase.detailContent!;

  // "YYYY-MM" → sprachrichtiger Monatsname + Jahr (z.B. "März 2025" / "March 2025")
  const formatMonthYear = (isoMonth: string) => {
    const match = /^(\d{4})-(\d{2})$/.exec(isoMonth);
    if (!match) return isoMonth;
    return new Date(Number(match[1]), Number(match[2]) - 1, 1)
      .toLocaleDateString(ui.dateLocale, { month: "long", year: "numeric" });
  };

  useEffect(() => {
    setActivePage("work");
    // Smooth scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [setActivePage]);

  return (
    <div className="w-full py-4 md:py-16 transition-all duration-400 max-w-6xl mx-auto">
      <FloatingBackLink href={lp('/work')} label={ui.workDetail.backToOverview} />
      <Link href={lp('/work')} className="page-back no-select">← {ui.workDetail.backToOverview}</Link>

      <Hero workCase={workCase} subtitle={content.subtitle} />

      <div className="max-w-5xl mx-auto sm:px-6 lg:px-0">
        {workCase.date && (
          <p data-cy="work-case-date" className="text-white text-lg md:text-xl font-medium tracking-wide -mt-10 mb-12">
            {formatMonthYear(workCase.date)}
          </p>
        )}

        <MetaInfo
          title={workCase.title}
          tags={workCase.tags}
          link={workCase.link}
          appStoreUrl={workCase.appStoreUrl}
          websiteUrl={workCase.websiteUrl}
          projectLinks={workCase.projectLinks}
          links={workCase.links}
        />

        <div>
          <ContentSection
            title={ui.workDetail.challenge}
            content={content.challenge}
            colorScheme="purple"
          />

          <ContentSection
            title={ui.workDetail.solution}
            content={content.solution}
            colorScheme="pink"
          />

          {content.implementation && content.implementation.length > 0 && (
            <ContentSection
              title={ui.workDetail.implementation}
              content={content.implementation}
              colorScheme="cyan"
            />
          )}

          {content.codeExamples && content.codeExamples.length > 0 && (
            <CodeExampleSection
              codeExamples={content.codeExamples}
            />
          )}

          <ContentSection
            title={ui.workDetail.results}
            content={content.results}
            colorScheme="blue"
          />

          {content.demoVideo && (
            <DemoVideo
              src={content.demoVideo.src}
              poster={content.demoVideo.poster}
              title={content.demoVideo.title}
              caption={content.demoVideo.caption}
              width={content.demoVideo.width}
              height={content.demoVideo.height}
              delay={0.15}
            />
          )}

          {content.images && content.images.length > 0 && (
            <ImageGallery
              images={content.images}
            />
          )}
        </div>

        <TechnologiesSection slug={workCase.slug} delay={0.2} />

        <NavigationFooter />
      </div>
    </div>
  );
}